import { z } from 'zod'

export const shareLinkChannelValues = [
  'general',
  'instagram_bio',
  'instagram_story',
  'whatsapp',
  'facebook',
  'qr',
] as const
export type ShareLinkChannel = (typeof shareLinkChannelValues)[number]

export const shareEarningKindValues = ['visit', 'interest', 'signup', 'check_in', 'plan'] as const
export type ShareEarningKind = (typeof shareEarningKindValues)[number]

/** The steps that still earn; `interest` (an answer) is kept only for old rows. */
export type ShareRewardKind = Exclude<ShareEarningKind, 'interest'>

export const shareEarningStatusValues = ['pending', 'confirmed', 'held', 'rejected'] as const
export type ShareEarningStatus = (typeof shareEarningStatusValues)[number]

/** Credits one visitor who came through a share link earns the organizer, per step. */
export const SHARE_CASHBACK_CREDITS: Record<ShareRewardKind, number> = {
  visit: 5,
  signup: 100,
  plan: 20,
  check_in: 30,
}

/** Rules every plan shares: cap per business, waiting time, expiry and attribution window. */
export const SHARE_CASHBACK_RULES = {
  monthlyCap: 8000,
  pendingHours: 24,
  expiryDays: 90,
  signupWindowDays: 30,
  cookieDays: 30,
} as const

/**
 * The cashback offer runs until `endsAt` (2026-12-31 23:59 Europe/Vienna) or until all
 * businesses together have earned `poolCredits` since `startsAt`, whichever comes first.
 */
export const SHARE_CASHBACK_OFFER = {
  startsAt: '2026-10-25T23:00:00.000Z',
  endsAt: '2026-12-31T23:00:00.000Z',
  poolCredits: 500_000,
} as const

/** The first-party cookie `/s/<code>` sets: `<code>.<click id>`, nothing personal. */
export const SHARE_ATTRIBUTION_COOKIE = 'pulsify_share'

const SHARE_LINK_CODE_PATTERN = /^[a-z0-9]{6,16}$/
const SHARE_CLICK_ID_PATTERN = /^[A-Za-z0-9_-]{16,40}$/

export function isShareLinkCode(value: string): boolean {
  return SHARE_LINK_CODE_PATTERN.test(value)
}

export const shareLinkCodeSchema = z.string().regex(SHARE_LINK_CODE_PATTERN)
export const shareClickIdSchema = z.string().regex(SHARE_CLICK_ID_PATTERN)

/** Reads the attribution cookie's value; null when it is missing or malformed. */
export function parseShareAttribution(
  value: string | null | undefined,
): { code: string; clickId: string } | null {
  if (!value) return null
  const [code, clickId, ...rest] = value.split('.')
  if (rest.length > 0 || !code || !clickId) return null
  if (!SHARE_LINK_CODE_PATTERN.test(code) || !SHARE_CLICK_ID_PATTERN.test(clickId)) return null
  return { code, clickId }
}

export const shareTargetKindValues = ['event', 'profile'] as const

/** The page a visit beacon comes from; a place profile's links open its `listing` page. */
export const shareVisitTargetKindValues = [...shareTargetKindValues, 'listing'] as const
export type ShareVisitTargetKind = (typeof shareVisitTargetKindValues)[number]

/** Where a share link leads: the page path without locale, e.g. `/events/<slug>`. */
export const shareLinkTargetSchema = z.object({
  kind: z.enum(shareTargetKindValues),
  id: z.string().uuid(),
  path: z.string().startsWith('/'),
})
export type ShareLinkTarget = z.infer<typeof shareLinkTargetSchema>

export const shareVisitBeaconSchema = z.object({
  code: shareLinkCodeSchema,
  clickId: shareClickIdSchema,
  targetKind: z.enum(shareVisitTargetKindValues),
  targetId: z.string().uuid(),
})
export type ShareVisitBeacon = z.infer<typeof shareVisitBeaconSchema>

export const shareAttributionSchema = z.object({
  code: shareLinkCodeSchema,
  clickId: shareClickIdSchema,
})
export type ShareAttribution = z.infer<typeof shareAttributionSchema>

/**
 * Where a share link leads inside the app: the screen kind and the id it opens. A place
 * profile's links open its `listing`; `handle` is set for organizer profiles only.
 */
export const shareAppTargetSchema = z.object({
  kind: z.enum(shareVisitTargetKindValues),
  id: z.string().uuid(),
  handle: z.string().nullable(),
})
export type ShareAppTarget = z.infer<typeof shareAppTargetSchema>

/**
 * The app's visit beacon for a share link it opened. `installId` is a random id the app
 * keeps per install; it stands in for the device when nobody is signed in.
 */
export const shareAppVisitSchema = z.object({
  code: shareLinkCodeSchema,
  clickId: shareClickIdSchema,
  installId: shareClickIdSchema,
})
export type ShareAppVisit = z.infer<typeof shareAppVisitSchema>

const count = () => z.number().int().nonnegative()

export const shareLinkSchema = z.object({
  id: z.string().uuid(),
  channel: z.enum(shareLinkChannelValues),
  code: shareLinkCodeSchema,
  url: z.string().url(),
  visitsThisMonth: count(),
  creditsThisMonth: count(),
})
export type ShareLink = z.infer<typeof shareLinkSchema>

export const shareEarningEntrySchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(shareEarningKindValues),
  channel: z.enum(shareLinkChannelValues),
  status: z.enum(shareEarningStatusValues),
  credits: count(),
  createdAt: z.string().datetime(),
})
export type ShareEarningEntry = z.infer<typeof shareEarningEntrySchema>

/** Whether the offer still pays, until when, and how much room the pool and the month have left. */
export const shareCashbackOfferSchema = z.object({
  active: z.boolean(),
  endsAt: z.string().datetime(),
  poolRemaining: count(),
  monthlyCap: count(),
  monthlyRemaining: count(),
})
export type ShareCashbackOffer = z.infer<typeof shareCashbackOfferSchema>

/**
 * A target's share links. `visits` counts credited visits this Europe/Vienna month;
 * `credits` sums confirmed and still pending credits; `pendingCredits` is the pending part.
 */
export const shareLinksResponseSchema = z.object({
  links: z.array(shareLinkSchema),
  month: z.object({ visits: count(), credits: count(), pendingCredits: count() }),
  history: z.array(shareEarningEntrySchema),
  offer: shareCashbackOfferSchema,
})
export type ShareLinksResponse = z.infer<typeof shareLinksResponseSchema>

export const cashbackLotSchema = z.object({
  id: z.string().uuid(),
  amount: count(),
  remaining: count(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
})
export type CashbackLot = z.infer<typeof cashbackLotSchema>

/** Unexpired cashback of a wallet, soonest expiry first, and what it holds in total. */
export const cashbackSummarySchema = z.object({
  activeCredits: count(),
  lots: z.array(cashbackLotSchema),
  offer: shareCashbackOfferSchema,
})
export type CashbackSummary = z.infer<typeof cashbackSummarySchema>

export const adminShareEarningSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  businessName: z.string(),
  kind: z.enum(shareEarningKindValues),
  channel: z.enum(shareLinkChannelValues),
  targetTitle: z.string(),
  credits: count(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
})
export type AdminShareEarning = z.infer<typeof adminShareEarningSchema>

export const adminShareTopEarnerSchema = z.object({
  businessId: z.string().uuid(),
  businessName: z.string(),
  visits: count(),
  credits: count(),
  held: count(),
})
export type AdminShareTopEarner = z.infer<typeof adminShareTopEarnerSchema>

/** Held steps waiting for review, and the businesses earning the most this month. */
export const adminShareReviewResponseSchema = z.object({
  held: z.array(adminShareEarningSchema),
  topEarners: z.array(adminShareTopEarnerSchema),
})
export type AdminShareReviewResponse = z.infer<typeof adminShareReviewResponseSchema>

export const adminShareDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject']),
})
export type AdminShareDecision = z.infer<typeof adminShareDecisionSchema>
